// Copyright (c) 2024, WSO2 LLC. (https://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 LLC. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.

import React from "react";
import { makeStyles } from "@mui/styles";
import { IconButton, Tooltip, Chip } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { Check } from "@mui/icons-material";
import { Link } from "react-router-dom";
import { STATUS_COLORS } from "../utils/AppConstants";
import { isAccessGranted } from "../utils/utils";
import {
  Privileges,
  PAGES,
  RequestStatus,
  EngagementStatus,
} from "../../Config";
import TableComponent from "../TableComponent";

const useStyles = makeStyles(() => ({
  formControl: {
    fontSize: 13,
    padding: 0,
    margin: 0,
  },
  statusChip: {
    padding: 2,
    borderRadius: 25,
    alignItems: "center",
    display: "block",
  },
  statusDot: {
    height: 10,
    width: 10,
    borderRadius: "50%",
    display: "inline-block",
    marginRight: 10,
    backgroundColor: "red",
  },
}));

const ApproveTable = (props) => {
  const {
    data,
    isLoading,
    isInternalEngagement,
    isCustomerEngagement,
    setTablePageSize,
    fetchData,
    setFilterText,
    setSortBy,
  } = props;

  const classes = useStyles();

  const buildActionColumn = () => {
    return {
      Header: "Action",
      accessor: "action",
      collapse: true,
      disableSortBy: true,
      Cell: ({ row }) => {
        return (
          <div style={{ display: "flex", justifyContent: "center" }}>
            {isAccessGranted([
              Privileges.APPROVE_INTERNAL_ENGAGEMENTS_EDIT,
              Privileges.APPROVE_MARKETING_EVENTS_EDIT,
            ]) && (
              <Tooltip title="Edit Engagement">
                <IconButton
                  aria-label="edit"
                  component={Link}
                  to={
                    isInternalEngagement
                      ? `${PAGES.APPROVE_INTERNAL_EDIT}/${row.original.engagementId}`
                      : `${PAGES.APPROVE_MARKETING_EDIT}/${row.original.engagementId}`
                  }
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {isAccessGranted(
              isInternalEngagement
                ? [Privileges.APPROVE_INTERNAL_ENGAGEMENTS_APPROVE]
                : [Privileges.APPROVE_MARKETING_EVENTS_APPROVE]
            ) && (
              <Tooltip title="View and Approve Engagement">
                <IconButton
                  aria-label="edit"
                  component={Link}
                  to={
                    isInternalEngagement
                      ? `${PAGES.APPROVE_INTERNAL_APPROVE}/${row.original.engagementId}`
                      : `${PAGES.APPROVE_MARKETING_APPROVE}/${row.original.engagementId}`
                  }
                >
                  <Check fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </div>
        );
      },
    };
  };

  const buildColumns = () => {
    const columns = [
      {
        Header: "Engagement ID",
        accessor: "engagementId",
        collapse: true,
        maxWidth: 120,
      },
      {
        Header: "Account",
        accessor: "customerName",
        collapse: true,
        minWidth: 120,
      },
      {
        Header: "Opportunity",
        accessor: "opportunityName",
        collapse: true,
        minWidth: 130,
      },
      {
        Header: "Engagement Code",
        accessor: "engagementCode",
        collapse: true,
        minWidth: 120,
      },
      {
        Header: "Allocation Type",
        accessor: "allocationTypeName",
        collapse: true,
      },
      {
        Header: "Engagement Location",
        accessor: "engagementNature",
        collapse: true,
        maxWidth: 140,
      },
      {
        Header: "Country",
        accessor: "country",
        collapse: true,
        maxWidth: 140,
      },
      {
        Header: "Start Date",
        accessor: "startDate",
        collapse: true,
        maxWidth: 90,
      },
      {
        Header: "End Date",
        accessor: "endDate",
        collapse: true,
        maxWidth: 90,
      },
      {
        Header: "Request Status",
        accessor: "requestStatus",
        minWidth: 150,
        style: {
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        },
        disableSortBy: true,
        Cell: ({ row }) => {
          if (row) {
            return (
              <Chip
                label={RequestStatus.PENDING_APPROVAL}
                style={{
                  borderRadius: 8,
                  height: 25,
                  width: 130,
                }}
              />
            );
          }
          return null;
        },
        collapse: true,
      },
      {
        Header: "Engagement Status",
        accessor: "engagementStatus",
        disableSortBy: true,
        Cell: ({ row }) => {
          if (row) {
            return (
              <div style={{ display: "flex", alignItems: "center" }}>
                <span
                  className={classes.statusDot}
                  style={{
                    backgroundColor: getEngagementStatusColor(
                      row.original.engagementStatus
                    ),
                  }}
                ></span>
                <span style={{ color: "black" }}>
                  {EngagementStatus.PENDING}
                </span>
              </div>
            );
          }
          return null;
        },
        collapse: true,
        minWidth: 120,
        style: { display: "flex", alignItems: "center" },
      },
    ];

    const hasPrivileges = isAccessGranted(
      isInternalEngagement
        ? [
            Privileges.APPROVE_INTERNAL_ENGAGEMENTS_EDIT,
            Privileges.APPROVE_INTERNAL_ENGAGEMENTS_APPROVE,
          ]
        : [
            Privileges.APPROVE_MARKETING_EVENTS_EDIT,
            Privileges.APPROVE_MARKETING_EVENTS_APPROVE,
          ]
    );

    if (hasPrivileges) {
      columns.unshift(buildActionColumn());
    }

    return columns;
  };

  const columns = React.useMemo(buildColumns, [isInternalEngagement]);

  const getEngagementStatusColor = (status) => {
    return STATUS_COLORS[status] || "red";
  };

  return (
    <TableComponent
      data={data}
      fetchData={fetchData}
      isLoading={isLoading}
      columns={columns}
      onPageSizeChange={setTablePageSize}
      setFilterText={setFilterText}
      setSortBy={setSortBy}
      showGlobalSearch={false}
    />
  );
};

export default ApproveTable;
